import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Chart } from 'primereact/chart';
import 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { jsPDF } from 'jspdf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { Skeleton } from 'primereact/skeleton';
import { exportTimestamp, exportedOnText } from 'shared/export/stamp';

import './AppChart.scss';

const ChartTitleIcon: React.FC<{
  icon: string;
  className: string;
  color?: string;
}> = ({ icon, className, color }) => {
  if (icon.includes('fa-')) {
    const name = icon
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.startsWith('fa-'))
      .map((t) => t.replace(/^fa-/, ''))
      .filter((t) => !['solid', 'regular', 'brands', 'light'].includes(t))
      .pop();
    const def = name
      ? fas[
          `fa${name
            .split('-')
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
            .join('')}` as keyof typeof fas
        ]
      : undefined;
    if (def) {
      return (
        <FontAwesomeIcon icon={def} className={className} style={{ color }} />
      );
    }
  }
  return <i className={`${className} ${icon}`} style={{ color }} />;
};

// ─────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────

export type AppChartType =
  | 'doughnut'
  | 'pie'
  | 'bar'
  | 'horizontalBar'
  | 'line'
  | 'stackedBar'
  | 'combo'
  | 'radar'
  | 'scatter';

/** A slice for doughnut/pie charts. */
export interface ChartSlice {
  label: string;
  value: number;
  color: string;
  /** Optional absolute count shown in the Table View report (e.g. students). */
  count?: number;
  /**
   * Force this slice's % label to show even when its value is 0 (and give it a
   * minimum-width arc to anchor the leader line). Use for value-only rings
   * where a zero is a real figure worth stating outright — e.g. the vs-Norms
   * ring reading "Shortfall 0%" rather than dropping the slice silently.
   */
  alwaysLabel?: boolean;
}

/** A series for bar/line/stacked/combo charts. */
export interface ChartSeries {
  label: string;
  data: number[];
  /**
   * Optional ORIGINAL (unscaled) values shown in the Table View, one per label.
   * Use when `data` is scaled for plotting — e.g. the Mark Band chart plots in
   * lakhs for a readable Y axis, but the table should list the real student
   * counts. When omitted, the Table View falls back to `data`.
   */
  tableData?: number[];
  color: string;
  /** Per-bar colors (bar charts only) — one color per data point. Overrides `color`. */
  barColors?: string[];
  /** Optional per-bar colors (overrides `color` when provided). */
  colors?: string[];
  /** For combo charts: render this series as a line instead of bars. */
  asLine?: boolean;

  stackGroup?: string;
  /** For combo charts: bind to the secondary right-hand axis. */
  useRightAxis?: boolean;
  /** Line series only: draw the line dashed (e.g. a difference line). */
  dashed?: boolean;
  /** Line series only: plot the points without a connecting line. */
  pointsOnly?: boolean;
  /**
   * Line series only: show this series' value above each point, even when the
   * series is on the right axis (e.g. a Compliance % line over bars). Optional
   * `pointLabelSuffix` is appended to the value (e.g. '%').
   */
  showPointLabels?: boolean;
  /** Suffix appended to each point label when `showPointLabels` is set. */
  pointLabelSuffix?: string;
}

/** A point for scatter charts. */
export interface ScatterPoint {
  x: number;
  y: number;
  /** Optional label shown in the tooltip (e.g. a district name). */
  label?: string;
}

/** A group (series) of scatter points sharing a color/legend entry. */
export interface ScatterGroup {
  label: string;
  color: string;
  points: ScatterPoint[];
}

/** Toolbar icon button config. */
export interface ChartTool {
  icon: string; // PrimeIcons class
  label: string;
  onClick?: () => void;
}

export interface AppChartProps {
  /** Chart flavour. */
  type: AppChartType;

  // ── Card header ──
  title?: string;
  subtitle?: string;
  /** Leading PrimeIcons class (e.g. 'pi pi-chart-pie'). */
  icon?: string;
  iconColor?: string;
  /** Show a checkbox-style badge before the title. */
  showCheck?: boolean;
  /** Extra suffix text after the title (e.g. "September 2026-27 highlighted"). */
  titleSuffix?: string;
  /** Inline element rendered right AFTER the title text (e.g. a mini legend). */
  titleExtra?: React.ReactNode;
  /** Element rendered on the right of the header (e.g. a dropdown). */
  headerRight?: React.ReactNode;
  /** Toolbar buttons. Pass [] to hide. Defaults to refresh/report/export. */
  tools?: ChartTool[];

  // ── Data ──
  /** For doughnut/pie: the slices. */
  slices?: ChartSlice[];
  /** For bar/line/stacked/combo: category labels. */
  labels?: string[];
  /** For bar/line/stacked/combo: the series. */
  series?: ChartSeries[];
  /** For scatter: the point groups. */
  scatterGroups?: ScatterGroup[];
  /**
   * Page the X AXIS: show at most this many labels at a time, with a
   * ◀ page/total ▶ pager under the chart. Use on dense charts such as
   * 30 days × 12 districts, where every series should stay on screen but the
   * category axis is too crowded to read.
   *
   * Only the labels and each series' per-label arrays (data / barColors /
   * colors) are windowed — the series LIST is untouched, so the legend keeps
   * listing every series and Table View still exports the full range.
   */
  labelsPerPage?: number;
  /**
   * Paginate the legend (default true, 12 entries per page).
   *
   * Pass false to list EVERY series at once instead. Use it when the series
   * are the thing the reader scans — e.g. all 38 districts on the attendance
   * trend — so they do not have to click through legend pages to find one.
   * The legend container wraps, so a long list simply becomes a few rows.
   */
  paginateLegend?: boolean;

  // ── Options ──
  /** Show % labels around doughnut/pie slices. Default true for those types. */
  showPercentLabels?: boolean;
  /**
   * Show a value label at the end of each bar (bar / horizontalBar), formatted
   * by this function (e.g. "10.8 L"). Omit for no bar labels.
   */
  barValueFormat?: (value: number) => string;
  /** Draw a dashed reference line at this value (e.g. the average). */
  averageLine?: number;
  /** X axis title (scatter). */
  xTitle?: string;
  /** Suffix on x-axis ticks (scatter), e.g. "%". */
  xTickSuffix?: string;
  /** Left axis title. */
  yTitle?: string;
  /** Suffix on left-axis ticks (e.g. " L"). */
  yTickSuffix?: string;
  /**
   * Pin the LEFT value axis maximum (e.g. 100 for a 0–100 % scale). Use for
   * bar/line/combo charts whose values are percentages or scores capped at a
   * known ceiling, so bars sit at a realistic height and the axis always runs
   * to the ceiling regardless of the data's own max.
   */
  yMax?: number;
  /** Right axis title (combo). */
  y1Title?: string;
  /** Suffix on right-axis ticks (combo), e.g. "%". */
  y1TickSuffix?: string;
  /**
   * Fixed max for the RIGHT axis (combo). Overrides the default (100 for a "%"
   * axis), e.g. cap a Share% axis at 50.
   */
  y1Max?: number;
  /** Highlight one label's bar in a darker shade (stacked/bar). */
  highlightLabel?: string;
  /** Darker highlight color. */
  highlightColor?: string;
  /**
   * Per-category rich tooltip. Keyed by the category label; each entry is the
   * list of lines to show. Replaces the default per-series tooltip (bar/combo).
   */
  tooltipDetails?: Record<string, { label: string; value: string }[]>;
  /**
   * Sort the (default) multi-series tooltip rows by the hovered point's value.
   * 'desc' = highest first, 'asc' = lowest first. Ignored when tooltipDetails
   * is set. Useful for line charts with many series (e.g. districts).
   */
  tooltipSort?: 'asc' | 'desc';
  /**
   * Draw a crosshair indicator line at the hovered point, synced with the
   * tooltip (vertical for vertical bar/line/combo/stacked charts, horizontal
   * for a horizontalBar). Defaults to ON for line and combo charts, OFF
   * otherwise. Set explicitly to override.
   */
  tooltipLine?: boolean;
  /**
   * When set, the chart body is replaced by this centered message (e.g. when
   * there is no data / not enough points to plot). The header/toolbar stay.
   */
  emptyMessage?: string;

  /** Radar chart tooltip header (e.g. "Tamil Nadu", "Chennai"). */
  radarTooltipScope?: string;

  /** Legend position for bar/line/combo/stacked charts. Default 'bottom'. */
  legendPosition?: 'top' | 'bottom';

  /** Compact large y-axis counts as 1.2 Cr / 3.4 L / 5.6 K. */
  compactY?: boolean;
  /** Show the data value on top of each bar. */
  showValueLabels?: boolean;
  /**
   * Custom on-bar label text, indexed by category (same order as `labels`).
   * Each entry can be a string or an array of strings for a multi-line label
   * (e.g. ["4,417", "26.7%"]). Requires `showValueLabels`. When omitted, the
   * numeric value is formatted via compactY / yTickSuffix.
   */
  valueLabels?: (string | string[])[];
  /** Show the legend (bar/line/combo). Default true. */
  showLegend?: boolean;
  /**
   * Render the legend as a scrollable HTML block ABOVE the chart (instead of
   * the canvas legend below it). Useful when there are many series. Defaults to
   * ON automatically for cartesian charts with more than 10 series; set
   * explicitly to force on/off. Clicking an entry toggles that series.
   */
  scrollableLegend?: boolean;
  /** Show a loader over the chart body while its data is being fetched. */
  loading?: boolean;

  /** Chart body height in px. Defaults to 240. */
  height?: number;
  /**
   * Grow the chart body height as the record count passes `autoHeightBaseline`.
   * On by default. With the defaults 12 records = base height, 24 = +40%,
   * 38 = ~+87%, scaling linearly with the extra records (capped at +150%).
   */
  autoHeight?: boolean;
  /** Record count treated as the base height (no scaling). Default 12. */
  autoHeightBaseline?: number;
  /** Height fraction added per full baseline of extra records. Default 0.4. */
  autoHeightStep?: number;
  /** Extra class names for the outer card. */
  className?: string;
  /**
   * Called when a bar/point is clicked (bar/line/combo/stacked charts).
   * Receives the category index and its label. Enables drill-down.
   */
  onBarClick?: (index: number, label: string) => void;
  /**
   * Called when a SERIES is clicked (a line or its point / a bar) on a
   * multi-series bar/line chart. Receives the clicked series' label (e.g. a
   * district or block name) and its index. Use for drill-down where each line
   * represents an entity rather than the x-axis category.
   */
  onSeriesClick?: (seriesLabel: string, seriesIndex: number) => void;
}

// ─────────────────────────────────────────────
// Defaults
// ─────────────────────────────────────────────

const defaultTools: ChartTool[] = [
  { icon: 'pi pi-refresh', label: 'Refresh' },
  { icon: 'pi pi-file', label: 'Table View' },
  { icon: 'pi pi-download', label: 'Download' },
];

const isCircular = (t: AppChartType) => t === 'doughnut' || t === 'pie';

// ─────────────────────────────────────────────
// Data builders
// ─────────────────────────────────────────────

// Give every real category (one carrying a `count`, e.g. Transgender) a small
// minimum drawn width — including 0-count ones — so it's a visible, hoverable
// wedge. Labels/tooltips still use each slice's TRUE value/count. Shared with
// the label-stagger calc below so "thin slice" detection matches the actual
// rendered arc angle, not just the raw percentage.
const MIN_DRAW = 1;
const isRealCircularCategory = (s: ChartSlice) =>
  s.count != null || s.alwaysLabel;
const drawnSliceValue = (s: ChartSlice): number =>
  s.value < MIN_DRAW && (s.value > 0 || isRealCircularCategory(s))
    ? MIN_DRAW
    : s.value;

function buildCircularData(slices: ChartSlice[]) {
  return {
    labels: slices.map((s) => s.label),
    datasets: [
      {
        data: slices.map(drawnSliceValue),
        backgroundColor: slices.map((s) => s.color),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 6,
      },
    ],
  };
}

/**
 * Several thin slices (each spanning a tiny arc) place their % label at
 * nearly the same point outside the ring, so the text overlaps or merges
 * into one unreadable blob (e.g. "0%" + "3%" + "63%" → "00363%"). This
 * computes, per data index, an EXTRA radial push-out distance so a run of
 * thin slices alternates between two radii — a near ring and a far ring —
 * giving each label breathing room instead of stacking on top of its
 * neighbour. Non-thin slices get 0 (no change to their existing position).
 *
 * IMPORTANT: the doughnut is a CIRCLE, so the first and last slices are
 * physically adjacent too — the ring wraps from the end back to the start
 * at the 12-o'clock mark. A run of thin slices can therefore straddle the
 * array boundary (e.g. the last slice is thin AND the first slice is thin).
 * To treat that correctly we rotate the scan to start at a non-thin slice
 * (so no run gets split by the array's arbitrary start/end) before walking
 * the circle and assigning alternating offsets within each run.
 */
const THIN_ARC_DEGREES = 14;
const LABEL_STAGGER_PX = 16;
function computeLabelStagger(values: number[]): number[] {
  const n = values.length;
  if (n === 0) return [];
  const total = values.reduce((sum, v) => sum + (v > 0 ? v : 0), 0) || 1;
  const isThin = values.map((v) => {
    const angleDeg = (Math.max(v, 0) / total) * 360;
    return angleDeg > 0 && angleDeg < THIN_ARC_DEGREES;
  });
  // Rotate the scan to begin right after a non-thin slice, so a thin run
  // that wraps across the array boundary is walked as one unbroken run
  // instead of being split into two unrelated runs that both reset to 0.
  // If every slice is thin (full ring of slivers), index 0 is as good a
  // start as any — the whole ring is one run either way.
  let startIdx = isThin.findIndex((t) => !t);
  if (startIdx === -1) startIdx = 0;
  else startIdx = (startIdx + 1) % n;

  const extra = new Array<number>(n).fill(0);
  let streak = 0;
  for (let k = 0; k < n; k++) {
    const i = (startIdx + k) % n;
    if (!isThin[i]) {
      streak = 0;
      continue;
    }
    // Every OTHER thin slice (in an unbroken run) is pushed out further, so
    // a cluster of thin slices fans across two radii instead of one.
    extra[i] = streak % 2 === 1 ? LABEL_STAGGER_PX : 0;
    streak += 1;
  }
  return extra;
}

function buildSeriesData(
  type: AppChartType,
  labels: string[],
  series: ChartSeries[],
  highlightLabel?: string,
  highlightColor?: string,
  showBarLabels?: boolean
) {
  const stacked = type === 'stackedBar';
  return {
    labels,
    datasets: series.map((s) => {
      const asLine = s.asLine || type === 'line';
      const bg =
        !asLine && s.barColors
          ? s.barColors
          : highlightLabel && !asLine
            ? labels.map((l) =>
                l === highlightLabel ? (highlightColor ?? s.color) : s.color
              )
            : s.color;

      if (asLine) {
        return {
          type: 'line' as const,
          label: s.label,
          data: s.data,
          borderColor: s.color,
          backgroundColor: s.color,
          borderWidth: 2,
          tension: 0.35,
          ...(s.dashed ? { borderDash: [6, 4] } : {}),
          ...(s.pointsOnly ? { showLine: false } : {}),
          // Keep an overlaid line/points series on the bars' category axis.
          ...(type === 'horizontalBar' ? { indexAxis: 'y' as const } : {}),
          pointRadius: s.pointsOnly ? 4 : 3,
          pointHoverRadius: 5,
          pointBackgroundColor: s.pointsOnly ? s.color : '#fff',
          pointBorderColor: s.color,
          pointBorderWidth: 2,
          fill: false,
          order: 1,
          yAxisID: s.useRightAxis ? 'y1' : 'y',
          // Point labels:
          //  • `showPointLabels` → always label this line's points (works on
          //    the right axis too, e.g. a Compliance % line over bars),
          //    positioned above the point with an optional suffix.
          //  • else when the caller enabled value labels and this line is on
          //    the LEFT axis, defer to the plugin-level datalabels config.
          //  • otherwise hide them.
          datalabels: s.showPointLabels
            ? {
                display: true,
                anchor: 'end' as const,
                align: 'top' as const,
                offset: 2,
                clamp: true,
                // clamp keeps the label's ANCHOR point inside the chart area;
                // without clip:false the plugin still hard-clips the
                // rendered text itself at that same boundary, slicing off the
                // top of any label near the axis max even though the layout
                // padding above reserves room for exactly this.
                clip: false,
                color: s.color,
                font: { size: 10, weight: 600 as const },
                formatter: (value: number) =>
                  `${value}${s.pointLabelSuffix ?? ''}`,
              }
            : showBarLabels && !s.useRightAxis
              ? undefined
              : { display: false },
        };
      }
      return {
        type: 'bar' as const,
        label: s.label,
        data: s.data,
        backgroundColor: s.colors ?? bg, // per-bar colors when provided
        borderRadius: 4,
        maxBarThickness: 22,
        // Thicker bars with a small, even gap (closer to the reference design).
        categoryPercentage: 0.85,
        barPercentage: 0.9,
        stack: stacked ? (s.stackGroup ?? 'stack') : undefined,
        order: 2,
        yAxisID: s.useRightAxis ? 'y1' : 'y',
        // Per-bar labels:
        //  • `showPointLabels` → label this series' bars with their value +
        //    optional suffix (e.g. a Compliance % series shown as bars in the
        //    "Bar view" toggle), even on the right axis.
        //  • else when the caller asked for end-of-bar values, defer to the
        //    plugin-level datalabels config.
        //  • otherwise hide them.
        datalabels: s.showPointLabels
          ? {
              display: true,
              anchor: 'end' as const,
              align: 'top' as const,
              offset: 2,
              clamp: true,
              clip: false,
              color: s.color,
              font: { size: 10, weight: 600 as const },
              formatter: (value: number) =>
                `${value}${s.pointLabelSuffix ?? ''}`,
            }
          : showBarLabels
            ? undefined
            : { display: false },
      };
    }),
  };
}

function buildRadarData(labels: string[], series: ChartSeries[]) {
  // Convert "#rrggbb" to an rgba() fill so the radar polygon is translucent.
  const toRgba = (hex: string, alpha: number): string => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
    if (!m) return hex;
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return {
    labels,
    datasets: series.map((s) => ({
      label: s.label,
      data: s.data,
      borderColor: s.color,
      backgroundColor: toRgba(s.color, 0.18),
      borderWidth: 2,
      pointRadius: 3,
      pointBackgroundColor: s.color,
      pointBorderColor: '#fff',
      pointBorderWidth: 1,
      fill: true,
      datalabels: { display: false },
    })),
  };
}

function buildScatterData(groups: ScatterGroup[]) {
  return {
    datasets: groups.map((g) => ({
      label: g.label,
      data: g.points.map((p) => ({ x: p.x, y: p.y, _label: p.label })),
      backgroundColor: g.color,
      borderColor: g.color,
      pointRadius: 6,
      pointHoverRadius: 8,
      datalabels: { display: false },
    })),
  };
}

// ─────────────────────────────────────────────
// Options builders
// ─────────────────────────────────────────────

interface ScatterOptionArgs {
  xTitle?: string;
  xTickSuffix: string;
  yTitle?: string;
  yTickSuffix: string;
}

function buildScatterOptions({
  xTitle,
  xTickSuffix,
  yTitle,
  yTickSuffix,
}: ScatterOptionArgs) {
  const axisTitle = (text?: string) =>
    text
      ? {
          display: true,
          text,
          font: { weight: 600 as const },
          color: '#374151',
        }
      : { display: false };

  return {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle' as const,
          padding: 12,
          boxWidth: 8,
          font: { size: 11 },
          color: '#4b5563',
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx: { raw: { x: number; y: number; _label?: string } }) => {
            const p = ctx.raw;
            const name = p._label ? `${p._label}: ` : '';
            return `${name}${p.x}${xTickSuffix} / ${p.y}${yTickSuffix}`;
          },
        },
      },
      datalabels: { display: false },
    },
    scales: {
      x: {
        type: 'linear' as const,
        position: 'bottom' as const,
        title: axisTitle(xTitle),
        ticks: {
          color: '#6b7280',
          callback: (value: number | string) => `${value}${xTickSuffix}`,
        },
        grid: { color: '#eef0f4' },
      },
      y: {
        title: axisTitle(yTitle),
        ticks: {
          color: '#6b7280',
          callback: (value: number | string) => `${value}${yTickSuffix}`,
        },
        grid: { color: '#eef0f4' },
      },
    },
  };
}

function buildRadarOptions(scopeName?: string) {
  return {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'rectRounded' as const,
          padding: 12,
          boxWidth: 10,
          font: { size: 11 },
          color: '#4b5563',
        },
      },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        callbacks: {
          title: (tooltipItems: any[]) => {
            // Show different header based on which dataset is being hovered
            if (tooltipItems && tooltipItems.length > 0) {
              const datasetIndex = tooltipItems[0].datasetIndex;
              const chart = tooltipItems[0].chart;
              const datasets = chart.data.datasets;

              // If hovering over the state average dataset (Tamil Nadu average)
              if (datasets[datasetIndex]?.label?.includes('Tamil Nadu')) {
                return 'TAMIL NADU';
              }
              // Otherwise show the current scope (district/block/school)
              return scopeName ? scopeName.toUpperCase() : 'TAMIL NADU';
            }
            return scopeName ? scopeName.toUpperCase() : 'TAMIL NADU';
          },
          beforeBody: (tooltipItems: any[]) => {
            // Get the hovered dataset
            if (tooltipItems && tooltipItems.length > 0) {
              const datasetIndex = tooltipItems[0].datasetIndex;
              const chart = tooltipItems[0].chart;
              const datasets = chart.data.datasets;
              const labels = chart.data.labels;

              // Build tooltip showing only the averages for each subject from the hovered dataset
              const lines: string[] = [];
              labels.forEach((label: string, index: number) => {
                const avgValue = datasets[datasetIndex]?.data[index];
                if (avgValue !== undefined) {
                  lines.push(`${label} ${avgValue}`);
                }
              });

              return lines;
            }
            return [];
          },
          label: () => '', // Hide individual point labels
        },
      },
      datalabels: { display: false },
    },
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          color: '#9ca3af',
          font: { size: 9 },
          backdropColor: 'transparent',
        },
        grid: { color: '#e5e7eb' },
        angleLines: { color: '#e5e7eb' },
        pointLabels: {
          color: '#374151',
          font: { size: 11 },
        },
      },
    },
  };
}

/**
 * Format a percentage for display: a genuine zero shows as "0%", values that
 * round to a whole number show without decimals (e.g. "49%", "51%"). A tiny
 * non-zero share that would round to 0 keeps one significant digit (e.g.
 * "0.01%") so the slice doesn't read as 0.
 */
const fmtPct = (value: number): string => {
  if (value === 0) return '0%';
  const rounded = Math.round(value);
  if (rounded >= 1) return `${rounded}%`;
  // Sub-1% share → show the original (small) value so it isn't shown as "0%".
  return `${value}%`;
};

interface CircularLegendControl {
  /** All original slices (incl. hidden) so the legend can list every entry. */
  allSlices: ChartSlice[];
  /** Labels currently toggled off. */
  hidden: Set<string>;
  /** Toggle a slice's visibility by label. */
  onToggle: (label: string) => void;
  /** True when every slice is hidden (placeholder ring is shown). */
  placeholder?: boolean;
}

/** Compact large counts in the Indian system: 1.2 Cr, 3.4 L, 5.6 K. */
const compactIndian = (n: number): string => {
  const abs = Math.abs(n);
  if (abs >= 1e7) return `${(n / 1e7).toFixed(1)} Cr`;
  if (abs >= 1e5) return `${(n / 1e5).toFixed(1)} L`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)} K`;
  return `${n}`;
};

function buildCircularOptions(
  showPercentLabels: boolean,
  slices: ChartSlice[] = [],
  legend?: CircularLegendControl
) {
  // Precomputed per-slice extra push-out so a run of thin, crowded slices
  // fans their % labels across two radii instead of overlapping at one.
  const labelStagger = computeLabelStagger(slices.map(drawnSliceValue));
  // Staggered (crowded-thin-slice) labels are pushed an EXTRA LABEL_STAGGER_PX
  // beyond the normal offset. `clamp: true` on the datalabels keeps every
  // label inside the chart area — so if the padding doesn't reserve room for
  // that extra push, the staggered label gets clamped right back onto its
  // neighbour's position, undoing the stagger entirely (this is what caused
  // "0.36%" to swallow the second slice's label near the 12 o'clock seam).
  // Reserving the stagger amount on every side guarantees the pushed-out
  // label always has somewhere to actually go, regardless of which part of
  // the ring the crowded run sits on.
  const hasStagger = labelStagger.some((v) => v > 0);
  const staggerRoom = hasStagger ? LABEL_STAGGER_PX : 0;
  return {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '62%',
    // Extra room on all sides for the outside labels + leader lines. The large
    // bottom padding reserves a clear band between the lowest slice label and
    // the legend so they never overlap.
    layout: {
      padding: {
        top: 34 + staggerRoom,
        right: 40 + staggerRoom,
        bottom: 44 + staggerRoom,
        left: 40 + staggerRoom,
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        // Empty legend title acts as a vertical spacer that pushes the legend
        // row DOWN, clear of the lowest slice's % label (which, for a near-full
        // slice, points straight down into the legend).
        title: {
          display: true,
          text: '',
          padding: { top: 18, bottom: 0 },
        },
        // Custom click: toggle the slice off/on and recompute the rest to 100%
        // (instead of Chart.js's default hide, which doesn't recompute).
        onClick: legend
          ? (_e: unknown, item: { text?: string }) => {
              if (item?.text) legend.onToggle(item.text);
            }
          : undefined,
        labels: {
          usePointStyle: true,
          pointStyle: 'rectRounded' as const,
          padding: 10,
          boxWidth: 10,
          font: { size: 11 },
          color: '#4b5563',
          // List EVERY original slice (so hidden ones can be re-enabled);
          // hidden entries are shown struck-through / greyed.
          generateLabels: legend
            ? () =>
                legend.allSlices.map((s, i) => {
                  const isHidden = legend.hidden.has(s.label);
                  return {
                    text: s.label,
                    fillStyle: s.color,
                    strokeStyle: s.color,
                    fontColor: isHidden ? '#b0b4bb' : '#4b5563',
                    hidden: isHidden,
                    index: i,
                    pointStyle: 'rectRounded' as const,
                  };
                })
            : undefined,
        },
      },
      tooltip: {
        // No tooltip for the placeholder ring.
        enabled: !legend?.placeholder,
        callbacks: {
          label: (ctx: { label?: string; dataIndex: number }) => {
            const slice = slices[ctx.dataIndex];
            const count = slice?.count;
            const pct = fmtPct(slice ? slice.value : 0);
            return count != null
              ? `${ctx.label}: ${count.toLocaleString('en-IN')} (${pct})`
              : `${ctx.label}: ${pct}`;
          },
        },
      },
      datalabels:
        showPercentLabels && !legend?.placeholder
          ? {
              display: (ctx: { dataIndex: number }) => {
                const slice = slices[ctx.dataIndex];
                if (!slice) return false;
                // A real category — one the caller gave a `count` for — is
                // labelled even when that count is zero, so e.g. "To be
                // demolished 0%" is stated outright instead of being dropped
                // and leaving the legend entry with no figure anywhere.
                // buildCircularData already pads such a slice to MIN_DRAW, so
                // it has an arc for the label's leader line to point at.
                if (slice.count != null || slice.alwaysLabel) return true;
                // Slices given only a value (no count, e.g. an ad-hoc ring)
                // keep the old rule: a zero there means "nothing to show"
                // rather than a counted zero.
                return (slice.value ?? 0) > 0;
              },
              color: '#374151',
              font: { size: 11, weight: 600 as const },
              // Push the label clearly OUTSIDE the ring so the leader line
              // (drawn by the doughnutLeaderLine plugin) has room and the text
              // doesn't collide with the arc or the bottom legend. Thin,
              // crowded slices (see labelStagger) get an EXTRA push so their
              // labels land on a second, farther ring instead of merging with
              // their neighbours' labels at the same radius.
              anchor: 'end' as const,
              align: 'end' as const,
              offset: (ctx: { dataIndex: number }) =>
                18 + (labelStagger[ctx.dataIndex] ?? 0),
              clamp: true,
              // Tiny share (would read 0.00%) → show the count instead.
              formatter: (_value: number, ctx: { dataIndex: number }) => {
                const slice = slices[ctx.dataIndex];
                const pct = slice?.value ?? 0;
                const count = slice?.count;
                if (pct > 0 && pct < 0.01 && count != null) {
                  return count.toLocaleString('en-IN');
                }
                return fmtPct(pct);
              },
            }
          : { display: false },
    },
  };
}

interface SeriesOptionArgs {
  type: AppChartType;
  xTitle?: string;
  yTitle?: string;
  yTickSuffix: string;
  yMax?: number;
  /** Largest value across series (horizontalBar) — pins x and x2 to one max. */
  horizontalValueMax?: number;
  y1Title?: string;
  y1TickSuffix: string;
  y1Max?: number;
  hasRightAxis: boolean;
  /** Per-category custom tooltip lines, keyed by the category label. */
  tooltipDetails?: Record<string, { label: string; value: string }[]>;
  /** Sort default multi-series tooltip rows by hovered value (asc/desc). */
  tooltipSort?: 'asc' | 'desc';
  /** Legend position (default 'bottom'). */
  legendPosition?: 'top' | 'bottom';
  compactY?: boolean;
  showValueLabels?: boolean;
  valueLabels?: (string | string[])[];
  showLegend?: boolean;
  /** A series opts into on-point labels — reserve top padding so they fit. */
  hasPointLabels?: boolean;
}

/**
 * External (HTML) tooltip that renders a scrollable list of all rows for the
 * hovered category — used when a chart has many tooltip rows (e.g. all 38
 * districts for a year) that would overflow a native tooltip.
 */
function renderListTooltip(
  context: {
    chart: { canvas: HTMLCanvasElement };
    tooltip: {
      opacity: number;
      title?: string[];
      caretX: number;
      caretY: number;
    };
  },
  details: Record<string, { label: string; value: string }[]>
) {
  const { chart, tooltip } = context;
  const id = 'app-chart-list-tooltip';
  let el = document.getElementById(id) as HTMLDivElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = id;
    el.style.position = 'fixed';
    el.style.zIndex = '9999';
    el.style.pointerEvents = 'auto';
    el.style.background = '#ffffff';
    el.style.color = '#374151';
    el.style.border = '1px solid #e5e7eb';
    el.style.borderRadius = '8px';
    el.style.padding = '8px 10px';
    el.style.fontSize = '12px';
    el.style.boxShadow = '0 8px 24px rgba(16,24,40,0.12)';
    el.style.maxHeight = '280px';
    el.style.overflowY = 'auto';
    el.style.minWidth = '200px';
    el.style.transition = 'opacity 0.1s ease';
    document.body.appendChild(el);
  }

  if (tooltip.opacity === 0) {
    el.style.opacity = '0';
    // Also stop the (now invisible) panel from capturing pointer events — it is
    // position:fixed at z-index 9999, so if it kept pointer-events:auto it would
    // sit over the card and swallow clicks (e.g. the Table View Close button).
    el.style.pointerEvents = 'none';
    return;
  }
  // Visible again → allow interaction (scrolling a long list).
  el.style.pointerEvents = 'auto';

  const title = tooltip.title?.[0] ?? '';
  const rows = details[title] ?? [];
  const header = `<div style="font-weight:700;margin-bottom:6px;position:sticky;top:0;background:#ffffff;padding-bottom:4px;color:#111827">${title}</div>`;
  const body = rows
    .map(
      (r) =>
        `<div style="display:flex;justify-content:space-between;gap:16px;padding:2px 0"><span style="color:#6b7280">${r.label}</span><span style="font-weight:600;color:#111827">${r.value}</span></div>`
    )
    .join('');
  el.innerHTML = header + body;

  const canvasRect = chart.canvas.getBoundingClientRect();
  el.style.opacity = '1';

  // Position relative to the hovered point, but keep the whole panel on-screen.
  // The panel defaults to the RIGHT of the cursor; if that would run past the
  // right edge of the viewport (e.g. the last bar), flip it to the LEFT so its
  // values aren't clipped. Clamp the top so it never overflows the bottom.
  const GAP = 12;
  const margin = 8;
  const pointX = canvasRect.left + tooltip.caretX;
  const pointY = canvasRect.top + tooltip.caretY;
  const panelW = el.offsetWidth;
  const panelH = el.offsetHeight;

  let left = pointX + GAP;
  if (left + panelW > window.innerWidth - margin) {
    // Flip to the left of the cursor.
    left = pointX - GAP - panelW;
    // If flipping still overflows the left edge, just clamp into view.
    if (left < margin) {
      left = Math.max(margin, window.innerWidth - margin - panelW);
    }
  }

  let top = pointY;
  if (top + panelH > window.innerHeight - margin) {
    top = Math.max(margin, window.innerHeight - margin - panelH);
  }

  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}

function buildSeriesOptions({
  type,
  xTitle,
  yTitle,
  yTickSuffix,
  yMax,
  horizontalValueMax,
  y1Title,
  y1TickSuffix,
  y1Max,
  hasRightAxis,
  tooltipDetails,
  tooltipSort,
  legendPosition = 'bottom',
  compactY = false,
  showValueLabels = false,
  valueLabels,
  showLegend = true,
  hasPointLabels = false,
}: SeriesOptionArgs) {
  const stacked = type === 'stackedBar';
  const horizontal = type === 'horizontalBar';

  // Round the data max up to a clean axis bound so the top (x2) and bottom (x)
  // value axes share ONE explicit max and therefore produce identical ticks.
  const niceCeil = (v: number): number => {
    if (!(v > 0)) return 1;
    const pow = Math.pow(10, Math.floor(Math.log10(v)));
    const step = pow / 2; // halves give nicer intermediate ticks (e.g. 10.9L→11L)
    return Math.ceil(v / step) * step;
  };
  const sharedValueMax =
    horizontal && horizontalValueMax != null && horizontalValueMax > 0
      ? niceCeil(horizontalValueMax)
      : undefined;

  // When per-category details are supplied, show a single rich tooltip block
  // (one line per detail) instead of the default per-series values.
  // Otherwise, optionally sort the default per-series rows by the hovered
  // point's value (e.g. districts tooltip in descending order).
  const tooltip = tooltipDetails
    ? {
        // Use an external HTML tooltip so a long list (e.g. all 38 entries for
        // the hovered year) renders in a compact, SCROLLABLE panel instead of a
        // giant native tooltip that overflows the screen.
        enabled: false,
        external: (context: {
          chart: { canvas: HTMLCanvasElement };
          tooltip: {
            opacity: number;
            title?: string[];
            caretX: number;
            caretY: number;
          };
        }) => renderListTooltip(context, tooltipDetails),
      }
    : tooltipSort
      ? {
          itemSort: (
            a: { parsed: { y: number } },
            b: { parsed: { y: number } }
          ) =>
            tooltipSort === 'desc'
              ? b.parsed.y - a.parsed.y
              : a.parsed.y - b.parsed.y,
        }
      : undefined;

  const axisTitle = (text?: string) =>
    text
      ? {
          display: true,
          text,
          font: { weight: 600 as const },
          color: '#374151',
        }
      : { display: false };

  return {
    maintainAspectRatio: false,
    responsive: true,
    indexAxis: horizontal ? ('y' as const) : ('x' as const),
    // Breathing room inside the plot area — a bit more on the left for a
    // horizontal bar so long category names (districts / blocks) have space.
    // For a vertical chart with on-bar value labels, add extra top padding so
    // tall bars + their labels don't collide with the legend above.
    layout: {
      padding: horizontal
        ? { top: 8, right: 56, bottom: 8, left: 12 }
        : {
            top: showValueLabels || hasPointLabels ? 28 : 8,
            right: 12,
            bottom: 8,
            left: 8,
          },
    },
    // For a horizontal bar the category runs along the Y axis, so resolve the
    // hovered item by Y (otherwise index-mode locks onto the first category and
    // every bar shows the same tooltip).
    interaction: {
      mode: 'index' as const,
      intersect: false,
      axis: horizontal ? ('y' as const) : ('x' as const),
    },
    plugins: {
      legend: {
        display: showLegend,
        position: legendPosition,
        labels: {
          usePointStyle: true,
          pointStyle: 'rectRounded' as const,
          padding: 14,
          boxWidth: 10,
          font: { size: 11 },
          color: '#4b5563',
        },
      },
      ...(tooltip ? { tooltip } : {}),
      datalabels: showValueLabels
        ? {
            display: (ctx: {
              dataset: { data: number[]; type?: string; yAxisID?: string };
              dataIndex: number;
            }) =>
              // Label the left-axis series (e.g. Attendance %) whether it is
              // drawn as bars OR a line, but skip any series on the right (y1)
              // axis — e.g. Absentee-days.
              ctx.dataset.yAxisID !== 'y1' &&
              Number(ctx.dataset.data[ctx.dataIndex]) !== 0,
            anchor: 'end' as const,
            align: horizontal ? ('right' as const) : ('end' as const),
            offset: 2,
            clamp: true,
            // Without clip:false the plugin hard-clips the label at the
            // chart area edge even though layout.padding above reserves
            // room for it — this is the "cut-off label" bug: a bar sitting
            // at/near the axis max gets its on-bar value sliced in half.
            clip: false,
            color: '#374151',
            font: { size: 10, weight: 600 as const },
            // When the caller supplies `valueLabels`, render that exact text
            // (array = multi-line, e.g. count on top and share % below).
            // Otherwise fall back to the formatted numeric value.
            formatter: (value: number, ctx: { dataIndex: number }) => {
              if (valueLabels) {
                const custom = valueLabels[ctx.dataIndex];
                if (custom != null) return custom;
              }
              // Mirror the Y-axis: a suffix axis keeps its suffix; plain large
              // counts compact to the Indian system so on-bar labels aren't
              // raw 6-/7-digit numbers either.
              if (yTickSuffix) return `${value}${yTickSuffix}`;
              const n = Number(value);
              return Math.abs(n) >= 1000 ? compactIndian(n) : `${value}`;
            },
          }
        : { display: false },
    },
    scales: {
      x: {
        stacked,
        title: axisTitle(xTitle),
        // For a horizontal bar the X axis holds the VALUES (append the unit
        // suffix); for vertical charts it holds the CATEGORY labels (show all,
        // rotate to fit).
        grid: horizontal ? { color: '#eef0f4' } : { display: false },
        beginAtZero: horizontal ? true : undefined,
        // Pin to the shared clean max so the top (x2) axis can use the exact
        // same bound and show identical ticks.
        ...(sharedValueMax != null ? { min: 0, max: sharedValueMax } : {}),
        ticks: horizontal
          ? {
              font: { size: 10 },
              color: '#6b7280',
              callback: (value: number | string) => {
                const n = Number(value);
                if (yTickSuffix) return `${value}${yTickSuffix}`;
                // Compact large value-axis ticks: 200000 → "2L", 50000 → "50K".
                if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
                if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
                return `${n}`;
              },
            }
          : {
              font: { size: 10 },
              color: '#6b7280',
              // Show EVERY category label (don't drop crowded ones).
              autoSkip: false,
              maxRotation: 60,
              minRotation: 0,
            },
      },
      // Mirror the value axis at the TOP for horizontal bars, so a long list of
      // bars keeps the scale visible without scrolling to the bottom. It uses
      // the SAME explicit min/max (sharedValueMax) as the bottom x axis, so
      // both ends produce the identical ticks for this chart. Grid off to
      // avoid doubled lines. Only added for horizontalBar when the max is known.
      ...(horizontal && sharedValueMax != null
        ? {
            x2: {
              type: 'linear' as const,
              position: 'top' as const,
              min: 0,
              max: sharedValueMax,
              grid: { display: false },
              ticks: {
                font: { size: 10 },
                color: '#6b7280',
                // Same formatting as the bottom value axis.
                callback: (value: number | string) => {
                  const n = Number(value);
                  if (yTickSuffix) return `${value}${yTickSuffix}`;
                  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
                  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
                  return `${n}`;
                },
              },
            },
          }
        : {}),
      y: {
        stacked,
        beginAtZero: horizontal ? undefined : true,
        // For a vertical percentage chart, pin the value axis to 0–100 so bars
        // sit at a realistic height (an 88% bar fills ~88% of the plot) instead
        // of auto-scaling tight to the data and looking oversized. An explicit
        // `yMax` (e.g. 100 for a Marks/% axis) wins over the suffix shortcut.
        ...(!horizontal && yMax != null
          ? { max: yMax }
          : !horizontal && yTickSuffix === '%'
            ? { max: 100 }
            : {}),
        title: axisTitle(yTitle),
        // For a horizontal bar the Y axis holds the CATEGORY names — show every
        // label and resolve the tick index to its label string (Chart.js
        // passes the index to the callback, not the label). For vertical charts
        // the Y axis holds VALUES (compact / suffix formatting).
        ...(horizontal ? { autoSkip: false } : {}),
        ticks: horizontal
          ? {
              color: '#374151',
              font: { size: 11 },
              autoSkip: false,
              callback(
                this: { getLabelForValue: (v: number) => string },
                value: number | string
              ) {
                return this.getLabelForValue(Number(value));
              },
            }
          : {
              color: '#6b7280',
              // Percentage charts: tick every 20 (0/20/40/60/80/100) so the
              // plot reads cleanly instead of a dense 10-step grid.
              ...(yTickSuffix === '%' ? { stepSize: 20 } : {}),
              callback: (value: number | string) => {
                // A % (or other explicit suffix) axis keeps its suffix as-is.
                if (yTickSuffix) return `${value}${yTickSuffix}`;
                // Otherwise ALWAYS compact large counts to the Indian system
                // (1 L / 1.2 Cr / 5 K) so the axis never shows raw 6-/7-digit
                // numbers. Small values (<1000) render plainly. This is global
                // now — previously it was gated behind the per-call compactY
                // prop, so most charts showed un-scaled numbers.
                const n = Number(value);
                return Math.abs(n) >= 1000 ? compactIndian(n) : `${value}`;
              },
            },
        grid: { color: '#eef0f4' },
      },
      ...(hasRightAxis
        ? {
            y1: {
              position: 'right' as const,
              beginAtZero: true,
              // Explicit y1Max wins; else a "%" axis defaults to 100.
              max:
                y1Max != null ? y1Max : y1TickSuffix === '%' ? 100 : undefined,
              title: axisTitle(y1Title),
              ticks: {
                color: '#6b7280',
                // Large count axes (e.g. Absentee-days) → compact Indian format
                // like "1.5 Cr" / "90.0 L"; percentage axes keep the % suffix.
                callback: (value: number | string) =>
                  y1TickSuffix === '%'
                    ? `${value}${y1TickSuffix}`
                    : compactIndian(Number(value)),
              },
              grid: { drawOnChartArea: false },
            },
          }
        : {}),
    },
  };
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

const AppChart: React.FC<AppChartProps> = ({
  type,
  title,
  subtitle,
  icon,
  iconColor = '#2f5fb0',
  showCheck = false,
  titleSuffix,
  titleExtra,
  headerRight,
  tools = defaultTools,
  slices = [],
  // Renamed: `labels` / `series` below are the WINDOWED values (see the
  // X-axis paging block), so everything downstream renders the current page.
  labels: labelsProp = [],
  series: seriesProp = [],
  scatterGroups = [],
  labelsPerPage,
  paginateLegend = true,
  showPercentLabels,
  barValueFormat,
  averageLine,
  xTitle,
  xTickSuffix = '',
  yTitle,
  yTickSuffix = '',
  yMax,
  y1Title,
  y1TickSuffix = '',
  y1Max,
  highlightLabel,
  highlightColor,
  tooltipDetails,
  tooltipSort,
  tooltipLine,
  emptyMessage,
  radarTooltipScope,
  legendPosition = 'bottom',
  compactY = false,
  showValueLabels = false,
  valueLabels,
  showLegend = true,
  scrollableLegend,
  loading = false,
  height = 240,
  autoHeight = true,
  autoHeightBaseline = 12,
  autoHeightStep = 0.5,
  className = '',
  onBarClick,
  onSeriesClick,
}) => {
  // Ref to the PrimeReact Chart wrapper so we can reach the Chart.js instance
  // (for refresh) and its <canvas> (for export).
  const chartRef = useRef<Chart>(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  // Export stamp frozen when the Download dialog opens, so the filename shown
  // in the dialog is byte-for-byte the one the saved file gets.
  const [downloadStamp, setDownloadStamp] = useState('');
  const [reportOpen, setReportOpen] = useState(false);

  // ── X-axis (label) paging ─────────────────────────────────────────────
  // Windows the category axis so a dense chart stays readable, e.g. 30 days
  // of 12 districts shown 15 days at a time. Slices the labels and each
  // series' per-label arrays only; the series LIST is left alone so the
  // legend still lists every series and all of them render on every page.
  const labelPaged = !!labelsPerPage && labelsPerPage > 0;
  const labelPageCount = labelPaged
    ? Math.max(1, Math.ceil(labelsProp.length / labelsPerPage))
    : 1;
  const [labelPage, setLabelPage] = useState(0);
  // Clamp rather than reset in an effect, so shrinking the range (e.g. the
  // period dropdown going 30 days → 7) can't leave us on a dead page.
  const safeLabelPage = Math.min(labelPage, labelPageCount - 1);
  const labelFrom = labelPaged ? safeLabelPage * labelsPerPage : 0;
  const labelTo = labelPaged ? labelFrom + labelsPerPage : labelsProp.length;
  const labelWindowed = labelPaged && labelPageCount > 1;

  const labels = useMemo(
    () => (labelWindowed ? labelsProp.slice(labelFrom, labelTo) : labelsProp),
    [labelsProp, labelWindowed, labelFrom, labelTo]
  );

  const series = useMemo(() => {
    if (!labelWindowed) return seriesProp;
    return seriesProp.map((s) => ({
      ...s,
      data: (s.data ?? []).slice(labelFrom, labelTo),
      // barColors / colors are per-label too, so they have to move with it.
      ...(Array.isArray(s.barColors)
        ? { barColors: s.barColors.slice(labelFrom, labelTo) }
        : {}),
      ...(Array.isArray(s.colors)
        ? { colors: s.colors.slice(labelFrom, labelTo) }
        : {}),
    }));
  }, [seriesProp, labelWindowed, labelFrom, labelTo]);
  // Optional override from the Bar / Line / Stacked view toolbar buttons.
  const [viewOverride, setViewOverride] = useState<
    'bar' | 'line' | 'stacked' | null
  >(null);
  // Doughnut/pie: slice labels the user toggled OFF via the legend. Hidden
  // slices are dropped and the remaining slices' percentages recompute to 100%.
  const [hiddenSlices, setHiddenSlices] = useState<Set<string>>(new Set());

  // The rich hover tooltip (tooltipDetails) renders into a shared body-level
  // div that is position:fixed at z-index 9999. If it was visible when Table
  // View opens, it would hover over the card and swallow clicks (e.g. the
  // Close button). Force it hidden + click-through whenever the report opens.
  useEffect(() => {
    if (!reportOpen) return;
    const el = document.getElementById('app-chart-list-tooltip');
    if (el) {
      el.style.opacity = '0';
      el.style.pointerEvents = 'none';
    }
  }, [reportOpen]);

  // The chart type actually rendered (respecting any Bar/Line/Stacked toggle).
  const effectiveType: AppChartType =
    viewOverride === 'stacked'
      ? 'stackedBar'
      : viewOverride === 'bar'
        ? 'bar'
        : viewOverride === 'line'
          ? 'line'
          : type;

  const circular = isCircular(effectiveType);
  const combo = effectiveType === 'combo';
  const radar = effectiveType === 'radar';
  const scatter = effectiveType === 'scatter';

  // Paginated HTML legend (below the chart) for cartesian charts with many
  // series — auto-on past 10 series, or forced via the `scrollableLegend` prop.
  const htmlLegendOn =
    !circular &&
    !radar &&
    !scatter &&
    (scrollableLegend ?? seriesProp.length > 10);
  // Series indices toggled OFF via the legend (HTML legend or the canvas one).
  const [hiddenSeries, setHiddenSeries] = useState<Set<number>>(new Set());
  // Toggle a series' visibility.
  //
  // This deliberately only updates REACT STATE; the hidden flag is then applied
  // to the datasets in `chartData` below. It used to call Chart.js's
  // setDatasetVisibility() directly, which kept the visibility inside the chart
  // INSTANCE instead of in the data we render from — so the moment anything
  // re-initialised the chart (opening the download dialog, a parent re-render,
  // a refetch) every series came back and the user's selection was lost.
  // Driving it from state means the selection is reproduced on every render and
  // survives re-initialisation, and exports match what is on screen.
  const toggleSeries = useCallback((index: number) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  // The HTML legend is paginated (a page of entries + ◀ page/total ▶ controls)
  // so a long legend never overflows / pushes the chart down.
  // 12 per page so the Attendance trend chart's default 12 districts all list
  // on one page — a legend that hid two of them read as if the CHART were
  // paginated by district, which is not what it does.
  // `paginateLegend={false}` turns paging off entirely and lists every series.
  const LEGEND_PER_PAGE = paginateLegend ? 12 : Math.max(1, seriesProp.length);
  // Driven by the UNPAGED series list: X-axis paging windows each series'
  // data, never the set of series, so the legend must list all of them.
  const legendPageCount = Math.max(
    1,
    Math.ceil(seriesProp.length / LEGEND_PER_PAGE)
  );
  const [legendPage, setLegendPage] = useState(0);
  // Keep the page in range when the series count changes (e.g. SHOW 20 → 10).
  const safeLegendPage = Math.min(legendPage, legendPageCount - 1);
  // Entries for the current page, each paired with its ORIGINAL series index
  // so toggling still targets the right dataset.
  const legendPageItems = useMemo(
    () =>
      seriesProp
        .map((s, i) => ({ s, i }))
        .slice(
          safeLegendPage * LEGEND_PER_PAGE,
          safeLegendPage * LEGEND_PER_PAGE + LEGEND_PER_PAGE
        ),
    [seriesProp, safeLegendPage]
  );

  // Offset of the current legend page within the (label-windowed) series list.
  const legendOffset = safeLegendPage * LEGEND_PER_PAGE;

  // The series actually RENDERED on the chart: narrowed to the current legend
  const pagedSeries = useMemo(
    () => series.slice(legendOffset, legendOffset + LEGEND_PER_PAGE),
    [series, legendOffset, LEGEND_PER_PAGE]
  );

  // Visible doughnut/pie slices with percentages RECOMPUTED against only the
  // visible ones, so toggling a slice off via the legend makes the rest total
  // 100% (reference-site behaviour). Hidden slices are removed entirely.
  // Only `value` is recomputed; `count` and `color` are preserved.
  const effectiveSlices: ChartSlice[] = useMemo(() => {
    if (!circular) return slices;
    const visible = slices.filter((s) => !hiddenSlices.has(s.label));
    const totalCount = visible.reduce((sum, s) => sum + (s.count ?? 0), 0);
    // Prefer recomputing from counts (exact); fall back to the given values.
    if (totalCount > 0) {
      return visible.map((s) => ({
        ...s,
        value: Math.round(((s.count ?? 0) / totalCount) * 10000) / 100,
      }));
    }
    const totalValue = visible.reduce((sum, s) => sum + s.value, 0);
    if (totalValue > 0) {
      return visible.map((s) => ({
        ...s,
        value: Math.round((s.value / totalValue) * 10000) / 100,
      }));
    }
    return visible;
  }, [circular, slices, hiddenSlices]);

  // All slices hidden → render a faint placeholder ring instead of a blank box.
  const allSlicesHidden =
    circular && slices.length > 0 && hiddenSlices.size >= slices.length;

  // All series hidden (bar/line/stacked/combo) OR all slices hidden (circular)
  // → there is nothing to export, so the Download tool is disabled. Mirrors
  // allSlicesHidden but for the category-chart case.
  const allSeriesHidden =
    !circular &&
    seriesProp.length > 0 &&
    hiddenSeries.size >= seriesProp.length;
  const nothingToExport = allSlicesHidden || allSeriesHidden;

  // Read the <canvas> from the PrimeReact Chart instance (call-time only).
  const readCanvas = (): HTMLCanvasElement | null => {
    const inst = chartRef.current as unknown as {
      getCanvas?: () => HTMLCanvasElement;
      getElement?: () => HTMLElement;
    } | null;
    if (inst?.getCanvas) return inst.getCanvas();
    return inst?.getElement?.()?.querySelector('canvas') ?? null;
  };

  // Refresh → return the chart to its original state: drop any Bar/Line/Stacked
  // view toggle (back to the default `type`), restore legend-hidden slices,
  // close the Table View, then re-run the entry animation.
  const handleRefresh = useCallback(() => {
    setViewOverride(null);
    setHiddenSlices(new Set());
    // Hidden SERIES are part of "the chart's original state" too, so Refresh
    // restores them alongside hidden slices.
    setHiddenSeries(new Set());
    setReportOpen(false);
    const inst = chartRef.current as unknown as {
      getChart?: () => { reset: () => void; update: () => void };
    } | null;
    const chart = inst?.getChart?.();
    if (chart) {
      chart.reset();
      chart.update();
    }
  }, []);

  // Filename stamp is shared with the grid exports (shared/export/stamp) so
  // the two can't drift apart again. Prefers the stamp frozen when the dialog
  // opened; falls back to "now" if a handler is somehow called without it.
  const stamp = () => downloadStamp || exportTimestamp();

  /** `Chart_Title_2026-10-07_00-45-12` — base name for every chart export. */
  const exportBaseName = () =>
    `${(title || 'chart').replace(/\s+/g, '_')}_${stamp()}`;

  const handleImageDownload = useCallback(
    (format: 'png' | 'jpg') => {
      const canvas = readCanvas();
      if (!canvas) return;

      // The canvas is in DEVICE pixels (retina ≈ 2x the CSS size), so scale
      // the caption with it or it comes out hairline-thin on a HiDPI export.
      const ratio = canvas.clientWidth ? canvas.width / canvas.clientWidth : 1;
      const fontPx = Math.max(11, Math.round(12 * ratio));
      // Footer strip added BELOW the plot so the caption never covers data.
      const footerH = Math.round(fontPx * 2.2);

      // Flatten onto white so transparent PNG areas aren't black in JPG.
      const out = document.createElement('canvas');
      out.width = canvas.width;
      out.height = canvas.height + footerH;
      const ctx = out.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.drawImage(canvas, 0, 0);
        // "Exported on …" bottom-left, chart title bottom-right when present.
        ctx.fillStyle = '#6b7280';
        ctx.font = `${fontPx}px Arial, Helvetica, sans-serif`;
        ctx.textBaseline = 'middle';
        const midY = canvas.height + footerH / 2;
        ctx.textAlign = 'left';
        ctx.fillText(exportedOnText(), Math.round(fontPx * 0.8), midY);
        if (title) {
          ctx.textAlign = 'right';
          ctx.fillText(title, out.width - Math.round(fontPx * 0.8), midY);
        }
      }

      const mime = format === 'jpg' ? 'image/jpeg' : 'image/png';
      const url = out.toDataURL(mime, 1);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${exportBaseName()}.${format}`;
      link.click();
      setDownloadOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [title, downloadStamp]
  );

  // Export the chart canvas into a PDF. Carries the export date + time both
  // in the filename and as a caption under the title, so a printed or
  // forwarded copy is still traceable.
  const handlePdfDownload = useCallback(() => {
    const canvas = readCanvas();
    if (!canvas) return;
    const img = canvas.toDataURL('image/png', 1);
    // Extra vertical room for the title + "Exported on" caption above the plot.
    const topOffset = title ? 66 : 48;
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [canvas.width + 40, canvas.height + topOffset + 28],
    });
    if (title) {
      pdf.setFontSize(16);
      pdf.setTextColor(31, 41, 55);
      pdf.text(title, 20, 36);
    }
    pdf.setFontSize(10);
    pdf.setTextColor(120);
    pdf.text(exportedOnText(), 20, title ? 54 : 32);
    pdf.addImage(img, 'PNG', 20, topOffset, canvas.width, canvas.height);
    pdf.save(`${exportBaseName()}.pdf`);
    setDownloadOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, downloadStamp]);

  // Default toolbar: wire refresh + download to built-in handlers.
  const resolvedTools: ChartTool[] = useMemo(() => {
    const openDownload = () => {
      // Freeze the stamp as the dialog opens so the filename previewed in the
      // dialog is exactly the one the saved file gets.
      setDownloadStamp(exportTimestamp());
      setDownloadOpen(true);
    };
    const openReport = () => setReportOpen(true);
    // Explicit view selection (like Line view): each button sets its own view.
    //  Bar view     → grouped bars
    //  Stacked view → stacked bars
    //  Line view    → line
    const showBar = () => setViewOverride('bar');
    const showLine = () => setViewOverride('line');
    const showStacked = () => setViewOverride('stacked');
    if (tools === defaultTools) {
      return [
        { icon: 'pi pi-refresh', label: 'Refresh', onClick: handleRefresh },
        { icon: 'pi pi-file', label: 'Table View', onClick: openReport },
        { icon: 'pi pi-download', label: 'Download', onClick: openDownload },
      ];
    }
    return tools.map((t) => {
      // Auto-wire the standard toolbar buttons the caller supplied.
      if (t.onClick) return t;
      const l = t.label.toLowerCase();
      if (l.includes('refresh')) return { ...t, onClick: handleRefresh };
      if (l.includes('report') || l.includes('table')) {
        return { ...t, onClick: openReport };
      }
      if (l.includes('stack')) return { ...t, onClick: showStacked };
      if (l.includes('bar')) return { ...t, onClick: showBar };
      if (l.includes('line')) return { ...t, onClick: showLine };
      if (l.includes('download') || l.includes('export')) {
        return { ...t, onClick: openDownload };
      }
      return t;
    });
  }, [tools, handleRefresh]);

  // Series shown in the report: only the ones currently listed in the legend
  // below (the active legend page). When the legend isn't paginated this is
  // every series, so non-paginated charts are unaffected; a paginated legend
  // (e.g. "Top 15 Districts") makes Table View show just the visible labels.
  const reportSeries = useMemo(
    () => legendPageItems.map(({ s }) => s),
    [legendPageItems]
  );

  // Rows for the Table View report. Doughnut/pie → Category/Students/Share;
  // bar/line/stacked/combo → one row per label with each (shown) series' value.
  const reportColumns = circular
    ? ['Category', 'Students', 'Share']
    : ['Label', ...reportSeries.map((sr) => sr.label)];

  // Year/label rows keep the full range (previous logic); only the SERIES set
  // is narrowed to what the legend currently displays.
  const reportRows = useMemo(() => {
    if (circular) {
      return slices.map((sl) => [
        sl.label,
        sl.count != null ? sl.count.toLocaleString('en-IN') : '—',
        fmtPct(sl.value),
      ]);
    }
    return labelsProp.map((lbl, i) => [
      lbl,
      // Prefer the series' original/unscaled tableData for the report so a
      // chart that plots scaled values (e.g. lakhs) still lists real counts.
      ...reportSeries.map((sr) =>
        Number((sr.tableData ?? sr.data)[i] ?? 0).toLocaleString('en-IN')
      ),
    ]);
  }, [circular, slices, labelsProp, reportSeries]);

  // combo/stacked share the y1 axis when any series opts into the right axis.
  const hasRightAxis = useMemo(
    () => series.some((s) => s.useRightAxis),
    [series]
  );

  // Auto-detect "no data": nothing to plot (no categories / series / slices)
  // or every value is zero / empty. Then show a friendly placeholder instead
  // of an empty axes box. An explicit `emptyMessage` from the caller wins.
  const autoEmpty = useMemo(() => {
    if (circular) {
      if (slices.length === 0) return true;
      return slices.every((s) => (s.value ?? 0) === 0 && (s.count ?? 0) === 0);
    }
    if (scatter) {
      return (
        scatterGroups.length === 0 ||
        scatterGroups.every((g) => g.points.length === 0)
      );
    }
    // bar / horizontalBar / line / stackedBar / combo / radar
    if (labels.length === 0 || series.length === 0) return true;
    return series.every((sr) =>
      (sr.data ?? []).every((v) => v == null || Number(v) === 0)
    );
  }, [circular, scatter, slices, scatterGroups, labels, series]);

  const effectiveEmptyMessage =
    emptyMessage ??
    (autoEmpty ? 'No data available for this section' : undefined);

  // Scale the body height up as the number of categories grows past the
  // baseline (default 12). e.g. 24 records -> +20%, 36 -> +40%. On by default,
  // but only for category charts (bar/line/stacked/combo) and capped so a very
  // large series can't balloon the card. Circular/radar/scatter keep a fixed
  // height. Callers can opt out with `autoHeight={false}` or tune the baseline/step.
  const effectiveHeight = useMemo(() => {
    const base = Number(height);
    const categoryChart = !circular && !radar && !scatter;
    if (!autoHeight || !categoryChart) return base;
    // The count that drives size is whichever grows: the category labels
    // (e.g. 38 districts on a bar axis) OR the number of plotted series
    // (e.g. 24 district lines on a multi-line trend chart).
    //
    // Uses the UNPAGED counts on purpose. Deriving the height from the current
    // page made the card resize on every page change, and that resize fed
    // straight back into Chart.js's ResizeObserver — the "ResizeObserver loop
    // completed with undelivered notifications" warning. A fixed height across
    // pages breaks that cycle.
    const count = Math.max(labelsProp.length, seriesProp.length);
    const baseline = Math.max(1, autoHeightBaseline);
    if (count <= baseline) return base;
    const rawFactor = 1 + ((count - baseline) / baseline) * autoHeightStep;
    // Cap growth at +150% so an unexpectedly large dataset stays reasonable.
    const factor = Math.min(rawFactor, 2.5);
    return Math.round(base * factor);
  }, [
    autoHeight,
    autoHeightBaseline,
    autoHeightStep,
    circular,
    radar,
    scatter,
    labelsProp.length,
    seriesProp.length,
    height,
  ]);

  const chartData = useMemo(() => {
    const built = buildChartData();
    // Apply legend-hidden series DECLARATIVELY. Chart.js honours
    // `dataset.hidden`, and because it now lives in the rendered data (rather
    // than in the chart instance), the user's legend selection is preserved
    // across every re-render and re-initialisation — including opening the
    // download dialog, which is what used to reset it.
    if (circular || hiddenSeries.size === 0) return built;
    const datasets = (built as { datasets?: unknown[] }).datasets;
    if (!Array.isArray(datasets)) return built;
    return {
      ...built,
      // Rendered dataset index `i` is page-local; map it back to the ORIGINAL
      // series index (legendOffset + i) so legend-hidden selections still
      // target the right series when the legend is paginated.
      datasets: datasets.map((ds, i) =>
        hiddenSeries.has(legendOffset + i)
          ? { ...(ds as Record<string, unknown>), hidden: true }
          : ds
      ),
    };

    function buildChartData() {
      if (circular) {
        // All slices hidden → a single faint placeholder ring (no data).
        if (allSlicesHidden) {
          return {
            labels: ['No data'],
            datasets: [
              {
                data: [1],
                backgroundColor: ['#eef0f4'],
                borderColor: '#ffffff',
                borderWidth: 2,
                hoverOffset: 0,
              },
            ],
          };
        }
        return buildCircularData(effectiveSlices);
      }
      if (radar) return buildRadarData(labels, series);
      if (scatter) return buildScatterData(scatterGroups);
      // When the user forces "Bar view", strip per-series asLine so everything
      // renders as bars; "Line view" is handled by effectiveType === 'line'.
      // Only the current legend page's series are drawn (pagedSeries).
      const seriesForType =
        effectiveType === 'bar'
          ? pagedSeries.map((sr) => ({ ...sr, asLine: false }))
          : pagedSeries;
      return buildSeriesData(
        effectiveType,
        labels,
        seriesForType,
        highlightLabel,
        highlightColor,
        !!barValueFormat || !!showValueLabels
      );
    }
  }, [
    circular,
    radar,
    scatter,
    scatterGroups,
    effectiveType,
    effectiveSlices,
    allSlicesHidden,
    // NOTE: `slices` is deliberately NOT a dependency — the circular branch
    // reads `effectiveSlices` (derived from it). Including the raw prop made
    // chartData change identity on every parent render, needlessly
    // re-initialising the chart.
    labels,
    series,
    pagedSeries,
    legendOffset,
    highlightLabel,
    highlightColor,
    barValueFormat,
    showValueLabels,
    hiddenSeries,
  ]);

  const toggleSlice = useCallback((label: string) => {
    setHiddenSlices((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const options = useMemo(() => {
    if (circular) {
      return buildCircularOptions(showPercentLabels ?? true, effectiveSlices, {
        allSlices: slices,
        hidden: hiddenSlices,
        onToggle: toggleSlice,
        placeholder: allSlicesHidden,
      });
    }
    if (radar) {
      return buildRadarOptions(radarTooltipScope);
    }
    if (scatter) {
      return buildScatterOptions({ xTitle, xTickSuffix, yTitle, yTickSuffix });
    }
    // For a horizontal bar, the largest value across all series. Used to pin
    // BOTH the bottom (x) and top (x2) value axes to the same explicit max so
    // their ticks are identical (the top mirrors the bottom exactly).
    const horizontalValueMax =
      effectiveType === 'horizontalBar'
        ? Math.max(
            0,
            ...series.flatMap((s) => (s.data ?? []).map((v) => Number(v) || 0))
          )
        : undefined;

    const seriesOptions = buildSeriesOptions({
      type: effectiveType,
      legendPosition,
      xTitle,
      yTitle,
      yTickSuffix,
      yMax,
      horizontalValueMax,
      y1Title,
      y1TickSuffix,
      y1Max,
      hasRightAxis,
      tooltipDetails,
      tooltipSort,
      compactY,
      showValueLabels,
      valueLabels,
      showLegend: showLegend && !htmlLegendOn,
      // showLegend,
      hasPointLabels: series.some((s) => s.showPointLabels),
    }) as Record<string, unknown>;

    // Route the CANVAS legend's clicks through React state as well. Chart.js's
    // default handler mutates visibility on the chart instance, which does not
    // survive a re-initialisation (that is the bug this replaces). Toggling our
    // own state instead keeps the canvas legend and the HTML legend on exactly
    // the same source of truth.
    const existingPlugins = (seriesOptions.plugins ?? {}) as Record<
      string,
      unknown
    >;
    const existingLegend = (existingPlugins.legend ?? {}) as Record<
      string,
      unknown
    >;
    return {
      ...seriesOptions,
      plugins: {
        ...existingPlugins,
        legend: {
          ...existingLegend,
          onClick: (
            _event: unknown,
            item: { datasetIndex?: number } | undefined
          ) => {
            if (typeof item?.datasetIndex === 'number') {
              toggleSeries(item.datasetIndex);
            }
          },
        },
      },
    };
  }, [
    circular,
    radar,
    scatter,
    effectiveType,
    showPercentLabels,
    slices,
    effectiveSlices,
    hiddenSlices,
    allSlicesHidden,
    toggleSlice,
    xTitle,
    xTickSuffix,
    yTitle,
    yTickSuffix,
    y1Title,
    y1TickSuffix,
    y1Max,
    hasRightAxis,
    tooltipDetails,
    tooltipSort,
    legendPosition,
    compactY,
    showValueLabels,
    valueLabels,
    showLegend,
    htmlLegendOn,
    series,
    toggleSeries,
  ]);

  // Merge a bar/point click handler into the options when a caller wants
  // drill-down. Uses Chart.js' onClick(event, elements) → category index.
  const optionsWithClick = useMemo(() => {
    let opts = options as Record<string, unknown>;

    // Pointer cursor whenever the mouse is over a data element that shows a
    // tooltip — EVERY chart type (bars, points, doughnut/pie slices), not just
    // drillable ones. Reverts to the default cursor off the data.
    const basePlugins = (opts.plugins as Record<string, unknown>) ?? {};
    const baseLegend = (basePlugins.legend as Record<string, unknown>) ?? {};
    opts = {
      ...opts,
      onHover: (
        evt: { native?: { target?: HTMLElement | null } },
        elements: Array<unknown>
      ) => {
        const target = evt?.native?.target;
        if (target) {
          target.style.cursor = elements.length ? 'pointer' : 'default';
        }
      },
      plugins: {
        ...basePlugins,
        legend: {
          ...baseLegend,
          // Pointer cursor over the (built-in) legend entries so it's clear
          // they're clickable to check/uncheck a series or slice.
          onHover: (evt: { native?: { target?: HTMLElement | null } }) => {
            const t = evt?.native?.target;
            if (t) t.style.cursor = 'pointer';
          },
          onLeave: (evt: { native?: { target?: HTMLElement | null } }) => {
            const t = evt?.native?.target;
            if (t) t.style.cursor = 'default';
          },
        },
      },
    };

    // End-of-bar value labels (e.g. "10.8 L"). Overrides the per-dataset
    // datalabels:{display:false} via the plugin-level options block.
    if (barValueFormat) {
      const mergedPlugins = {
        ...((opts.plugins as Record<string, unknown>) ?? {}),
        datalabels: {
          display: true,
          anchor: 'end' as const,
          align: 'end' as const,
          offset: 2,
          clamp: true,
          clip: false,
          color: '#374151',
          font: { size: 10, weight: 600 as const },
          formatter: (value: number) => barValueFormat(value),
        },
      };
      opts = { ...opts, plugins: mergedPlugins };

      // buildSeriesOptions only reserves the extra top padding these on-bar
      // labels need when `showValueLabels`/`hasPointLabels` is set — it has
      // no visibility into this `barValueFormat` prop (merged in here,
      // afterwards). On a vertical bar/combo chart that left just the 8px
      // default, so a bar sitting at/near the axis max had nowhere for its
      // label to go and got sliced off at the canvas edge. Bump it to match.
      if (effectiveType !== 'horizontalBar') {
        const baseLayout = (opts.layout as Record<string, unknown>) ?? {};
        const basePadding =
          (baseLayout.padding as Record<string, number>) ?? {};
        opts = {
          ...opts,
          layout: {
            ...baseLayout,
            padding: {
              ...basePadding,
              top: Math.max(Number(basePadding.top) || 0, 28),
            },
          },
        };
      }
    }

    if (onBarClick || onSeriesClick) {
      opts = {
        ...opts,
        onClick: (
          _evt: unknown,
          elements: Array<{ index: number; datasetIndex?: number }>
        ) => {
          if (!elements || elements.length === 0) return;
          const el = elements[0];
          // Series click (drill into the clicked line/bar's entity) takes
          // priority when wired — identify the dataset by its index.
          if (onSeriesClick && typeof el.datasetIndex === 'number') {
            const ds = seriesProp[el.datasetIndex];
            if (ds?.label) {
              onSeriesClick(ds.label, el.datasetIndex);
              return;
            }
          }
          if (onBarClick) {
            // Chart.js reports the index within the CURRENT page, so shift it
            // back into the caller's full label array before handing it over.
            const pageIdx = el.index;
            onBarClick(labelFrom + pageIdx, labels[pageIdx]);
          }
        },
      };
    }
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    options,
    onBarClick,
    onSeriesClick,
    seriesProp,
    labels,
    labelFrom,
    barValueFormat,
    effectiveType,
  ]);

  // primeReact chart type: circular + radar pass through; everything else is
  // 'bar' (line series inside combo are declared per-dataset via dataset.type).
  const primeType = circular
    ? effectiveType
    : radar
      ? 'radar'
      : scatter
        ? 'scatter'
        : effectiveType === 'line'
          ? 'line'
          : 'bar';

  // Attach the datalabels plugin only when needed (circular % labels).
  // Memoized so toggling unrelated state (e.g. the download modal) doesn't
  // hand the Chart a new array reference and force a re-render/re-animation.
  const plugins = useMemo(() => {
    const list: object[] = [];
    // Datalabels: circular % labels, end-of-bar value labels, or on-bar
    // value labels (showValueLabels).
    if (
      (circular && (showPercentLabels ?? true)) ||
      barValueFormat ||
      showValueLabels ||
      series.some((s) => s.showPointLabels)
    ) {
      list.push(ChartDataLabels);
    }
    // Leader lines from each doughnut/pie slice edge out to its % label.
    if (circular && (showPercentLabels ?? true)) {
      // Mirrors the datalabels `offset` stagger above, so a leader line
      // always reaches exactly as far as its label was pushed out — thin,
      // crowded slices get a longer line onto the second ring instead of
      // stopping short of their (farther) label.
      const labelStagger = computeLabelStagger(
        effectiveSlices.map(drawnSliceValue)
      );
      list.push({
        id: 'doughnutLeaderLine',
        afterDatasetsDraw: (chart: {
          ctx: CanvasRenderingContext2D;
          getDatasetMeta: (i: number) => {
            data: {
              x: number;
              y: number;
              startAngle: number;
              endAngle: number;
              outerRadius: number;
            }[];
          };
          data: { datasets: { data: number[] }[] };
        }) => {
          const { ctx } = chart;
          const meta = chart.getDatasetMeta(0);
          if (!meta?.data?.length) return;
          const values = chart.data.datasets[0]?.data ?? [];
          ctx.save();
          meta.data.forEach((arc, i) => {
            if (!(Number(values[i]) > 0)) return;
            const mid = (arc.startAngle + arc.endAngle) / 2;
            const cos = Math.cos(mid);
            const sin = Math.sin(mid);
            // Start at the arc's outer edge, draw out to the label radius with
            // a short horizontal elbow toward the label.
            const extra = labelStagger[i] ?? 0;
            const r0 = arc.outerRadius;
            const r1 = arc.outerRadius + 12 + extra;
            const x0 = arc.x + cos * r0;
            const y0 = arc.y + sin * r0;
            const x1 = arc.x + cos * r1;
            const y1 = arc.y + sin * r1;
            const x2 = x1 + (cos >= 0 ? 8 : -8);
            ctx.beginPath();
            ctx.moveTo(x0, y0);
            ctx.lineTo(x1, y1);
            ctx.lineTo(x2, y1);
            ctx.strokeStyle = '#9ca3af';
            ctx.lineWidth = 1;
            ctx.stroke();
          });
          ctx.restore();
        },
      });
    }
    // Dashed average reference line across the value axis.
    if (averageLine != null) {
      list.push({
        id: 'averageLine',
        afterDraw: (chart: {
          ctx: CanvasRenderingContext2D;
          chartArea: {
            top: number;
            bottom: number;
            left: number;
            right: number;
          };
          scales: Record<string, { getPixelForValue: (v: number) => number }>;
        }) => {
          const { ctx, chartArea, scales } = chart;
          const hb = effectiveType === 'horizontalBar';
          const scale = hb ? scales.x : scales.y;
          if (!scale) return;
          const px = scale.getPixelForValue(averageLine);
          ctx.save();
          ctx.beginPath();
          ctx.setLineDash([5, 4]);
          ctx.strokeStyle = '#374151';
          ctx.lineWidth = 1;
          if (hb) {
            ctx.moveTo(px, chartArea.top);
            ctx.lineTo(px, chartArea.bottom);
          } else {
            ctx.moveTo(chartArea.left, px);
            ctx.lineTo(chartArea.right, px);
          }
          ctx.stroke();
          ctx.restore();
        },
      });
    }

    // Crosshair "tooltip line" at the hovered point, synced with the tooltip.

    const cartesian =
      !circular && !radar && !scatter && effectiveType !== 'horizontalBar';
    const lineEnabled = tooltipLine ?? true;
    if (lineEnabled && (cartesian || effectiveType === 'horizontalBar')) {
      const horizontal = effectiveType === 'horizontalBar';
      list.push({
        id: 'tooltipLine',
        afterDraw: (chart: {
          ctx: CanvasRenderingContext2D;
          chartArea: {
            top: number;
            bottom: number;
            left: number;
            right: number;
          };
          getActiveElements?: () => Array<{
            element: { x: number; y: number };
          }>;
        }) => {
          const active = chart.getActiveElements?.() ?? [];
          if (active.length === 0) return;
          const { ctx, chartArea } = chart;
          const { x, y } = active[0].element;
          ctx.save();
          ctx.beginPath();
          ctx.setLineDash([4, 3]);
          ctx.lineWidth = 1;
          ctx.strokeStyle = 'rgba(55, 65, 81, 0.45)';
          if (horizontal) {
            ctx.moveTo(chartArea.left, y);
            ctx.lineTo(chartArea.right, y);
          } else {
            ctx.moveTo(x, chartArea.top);
            ctx.lineTo(x, chartArea.bottom);
          }
          ctx.stroke();
          ctx.restore();
        },
      });
    }
    return list;
  }, [
    circular,
    radar,
    scatter,
    showPercentLabels,
    effectiveSlices,
    barValueFormat,
    averageLine,
    effectiveType,
    tooltipLine,
    showValueLabels,
    series,
  ]);

  // Highlight the active view button (Bar/Line/Stacked) in the toolbar.
  const activeViewLabel =
    viewOverride === 'bar'
      ? 'bar'
      : viewOverride === 'line'
        ? 'line'
        : viewOverride === 'stacked'
          ? 'stack'
          : null;

  return (
    <div className={`app-chart ${className}`.trim()}>
      {(title || headerRight) && (
        <div className="app-chart__head">
          <div className="app-chart__title-wrap">
            {showCheck && (
              <span className="app-chart__check">
                <i className="pi pi-check" />
              </span>
            )}
            {icon && (
              <ChartTitleIcon
                icon={icon}
                className="app-chart__icon"
                color={iconColor}
              />
            )}
            <div className="app-chart__titles">
              <h6 className="app-chart__title">
                {title}
                {titleSuffix && (
                  <span className="app-chart__suffix"> {titleSuffix}</span>
                )}
                {titleExtra && (
                  <span className="app-chart__title-extra">{titleExtra}</span>
                )}
              </h6>
              {subtitle && <p className="app-chart__subtitle">{subtitle}</p>}
            </div>
          </div>

          <div className="app-chart__actions">
            {headerRight && (
              <div className="app-chart__header-right">{headerRight}</div>
            )}
            {!reportOpen && resolvedTools.length > 0 && (
              <div className="app-chart__toolbar">
                {resolvedTools
                  // Hide the "Line" view toggle when the chart's NATURAL type
                  // is already a line — offering "Line" on a line chart is a
                  // no-op. Uses the `type` prop (not effectiveType) so the
                  // button stays hidden even after the user toggles views.
                  .filter(
                    (t) =>
                      !(
                        type === 'line' &&
                        t.label.toLowerCase().includes('line')
                      )
                  )
                  .map((t) => {
                    const l = t.label.toLowerCase();
                    const isDownload =
                      l.includes('download') || l.includes('export');
                    // Disable Download when every series/slice is hidden —
                    // there is nothing to export.
                    const disabled = isDownload && nothingToExport;
                    const isActiveView =
                      (activeViewLabel === 'stack' && l.includes('stack')) ||
                      (activeViewLabel === 'bar' &&
                        l.includes('bar') &&
                        !l.includes('stack')) ||
                      (activeViewLabel === 'line' && l.includes('line'));
                    return (
                      <button
                        key={t.label}
                        type="button"
                        className={`app-chart__tool${
                          isActiveView ? ' is-active' : ''
                        }${disabled ? ' is-disabled' : ''}`}
                        aria-label={t.label}
                        disabled={disabled}
                        onClick={disabled ? undefined : t.onClick}
                        data-tip={t.label}
                      >
                        <i className={t.icon} />
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Inline Table View — shown in place of the chart when Report is open. */}
      {reportOpen ? (
        <div className="app-chart__report" style={{ height: '450px' }}>
          <p className="app-chart__report-title">Table View</p>

          <div className="app-chart__report-table-wrap">
            <table className="app-chart__report-table">
              <thead>
                <tr>
                  {reportColumns.map((col, i) => (
                    <th key={col} className={i === 0 ? 'is-left' : ''}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportRows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} className={ci === 0 ? 'is-left' : ''}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="app-chart__report-footer">
            <button
              type="button"
              className="app-chart__report-close"
              onClick={() => setReportOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      ) : loading ? (
        <div className="app-chart__body" style={{ height, padding: 8 }}>
          <Skeleton width="100%" height="100%" borderRadius="8px" />
        </div>
      ) : effectiveEmptyMessage ? (
        <div
          className="app-chart__body app-chart__empty"
          style={{
            height,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            color: '#9ca3af',
            fontSize: 13,
            textAlign: 'center',
          }}
        >
          <i className="pi pi-info-circle" style={{ fontSize: 22 }} />
          <span>{effectiveEmptyMessage}</span>
        </div>
      ) : (
        <>
          {/* Body sizing is driven by the UNPAGED label count so the card
              keeps a constant height while paging the X axis — see the
              effectiveHeight note about the ResizeObserver feedback loop. */}
          <div
            className="app-chart__body"
            style={{
              height: effectiveHeight,
              overflowY:
                effectiveType === 'horizontalBar' && labelsProp.length > 14
                  ? 'auto'
                  : 'visible',
            }}
          >
            <div
              style={{
                height:
                  effectiveType === 'horizontalBar' && labelsProp.length > 14
                    ? Math.max(effectiveHeight, labelsProp.length * 26)
                    : '100%',
                width: '100%',
              }}
            >
              {/* `key` is deliberately keyed off the UNPAGED label count:
                  keying it off the visible count remounted the whole canvas on
                  each page change, and a fresh canvas re-registers the resize
                  observer mid-layout. Chart.js updates the data in place
                  instead, which is both smoother and loop-free. */}
              <Chart
                ref={chartRef}
                key={`${effectiveType}-${labelsProp.length}-${slices.length}-${combo}`}
                type={primeType}
                data={chartData}
                options={optionsWithClick}
                plugins={plugins}
                style={{ height: '100%', width: '100%' }}
              />
            </div>
          </div>

          {/* X-axis pager — shown when `labelsPerPage` windows the category
              axis. Steps through the label range (e.g. 30 days, 15 at a
              time); every series stays on the chart on every page. */}
          {labelWindowed && (
            <div className="app-chart__axis-pager">
              <button
                type="button"
                aria-label="Previous labels"
                disabled={safeLabelPage === 0}
                onClick={() => setLabelPage((p) => Math.max(0, p - 1))}
              >
                <i className="pi pi-chevron-left" />
              </button>
              <span className="app-chart__axis-pageinfo">
                {labels[0]} – {labels[labels.length - 1]}
                <span className="app-chart__axis-pagecount">
                  {safeLabelPage + 1}/{labelPageCount}
                </span>
              </span>
              <button
                type="button"
                aria-label="Next labels"
                disabled={safeLabelPage >= labelPageCount - 1}
                onClick={() =>
                  setLabelPage((p) => Math.min(labelPageCount - 1, p + 1))
                }
              >
                <i className="pi pi-chevron-right" />
              </button>
            </div>
          )}

          {/* Paginated legend below the chart — shown when the HTML legend is
              active (many series). One page of entries + ◀ page/total ▶ pager.
              Clicking an entry toggles that series (same as the old legend). */}
          {htmlLegendOn && (
            <div className="app-chart__html-legend">
              <div className="app-chart__legend-items">
                {legendPageItems.map(({ s, i }) => (
                  <button
                    key={s.label}
                    type="button"
                    className={`app-chart__legend-item${
                      hiddenSeries.has(i) ? ' is-hidden' : ''
                    }`}
                    onClick={() => toggleSeries(i)}
                    title={s.label}
                  >
                    <span
                      className="app-chart__legend-swatch"
                      style={{ background: s.color }}
                    />
                    <span className="app-chart__legend-label">{s.label}</span>
                  </button>
                ))}
              </div>

              {legendPageCount > 1 && (
                <div className="app-chart__legend-pager">
                  <button
                    type="button"
                    aria-label="Previous labels"
                    disabled={safeLegendPage === 0}
                    onClick={() => setLegendPage((p) => Math.max(0, p - 1))}
                  >
                    <i className="pi pi-chevron-left" />
                  </button>
                  <span className="app-chart__legend-pageinfo">
                    {safeLegendPage + 1}/{legendPageCount}
                  </span>
                  <button
                    type="button"
                    aria-label="Next labels"
                    disabled={safeLegendPage >= legendPageCount - 1}
                    onClick={() =>
                      setLegendPage((p) => Math.min(legendPageCount - 1, p + 1))
                    }
                  >
                    <i className="pi pi-chevron-right" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Download Chart modal ── */}
      {downloadOpen && (
        <div
          className="chart-download"
          role="dialog"
          aria-modal="true"
          aria-label="Download chart"
          onClick={() => setDownloadOpen(false)}
        >
          <div
            className="chart-download__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="chart-download__header">
              <span className="chart-download__header-title">
                <i className="pi pi-download" />
                Download Chart
              </span>
              <button
                type="button"
                className="chart-download__close"
                aria-label="Close"
                onClick={() => setDownloadOpen(false)}
              >
                <i className="pi pi-times" />
              </button>
            </div>

            <div className="chart-download__body">
              <p className="chart-download__file-label">FILE NAME</p>
              {/* Shows the real stamped base name, so it is obvious the export
                  date + time are part of the file. */}
              <p className="chart-download__file-name">{exportBaseName()}</p>
              <p className="chart-download__file-note">
                <i className="pi pi-clock" /> {exportedOnText()}
              </p>

              <div className="chart-download__options">
                <button
                  type="button"
                  className="chart-download__option chart-download__option--pdf"
                  onClick={handlePdfDownload}
                >
                  <span className="chart-download__badge chart-download__badge--pdf">
                    <i className="pi pi-file-pdf" />
                  </span>
                  <span className="chart-download__type">PDF</span>
                  <span className="chart-download__ext">.pdf</span>
                </button>

                <button
                  type="button"
                  className="chart-download__option chart-download__option--png"
                  onClick={() => handleImageDownload('png')}
                >
                  <span className="chart-download__badge chart-download__badge--png">
                    <i className="pi pi-image" />
                  </span>
                  <span className="chart-download__type">PNG</span>
                  <span className="chart-download__ext">.png</span>
                </button>

                <button
                  type="button"
                  className="chart-download__option chart-download__option--jpg"
                  onClick={() => handleImageDownload('jpg')}
                >
                  <span className="chart-download__badge chart-download__badge--jpg">
                    <i className="pi pi-image" />
                  </span>
                  <span className="chart-download__type">JPG</span>
                  <span className="chart-download__ext">.jpg</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoized so a chart only re-renders when its own props change — changing one
// chart's dropdown won't re-render/re-animate the sibling charts.
export default React.memo(AppChart) as typeof AppChart;
